import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  PlusIcon,
  EyeIcon,
  PencilIcon,
  TrashIcon,
  MagnifyingGlassIcon,
  AcademicCapIcon,
  PlayIcon,
  UserGroupIcon,
  VideoCameraIcon,
  DocumentIcon,
  PresentationChartLineIcon,
  PhotoIcon
} from '@heroicons/react/24/outline';
import { LearningSection, Team } from '../../types';
import { learningAPI, teamsAPI } from '../../services/api';
import Layout from '../../components/layout/Layout';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import LearningSectionModal from '../../components/admin/LearningSectionModal';
import LearningVideoModal from '../../components/admin/LearningVideoModal';
import LearningContentModal from '../../components/admin/LearningContentModal';
import LearningSectionDetailModal from '../../components/admin/LearningSectionDetailModal';
import ConfirmDialog from '../../components/common/ConfirmDialog';
import toast from 'react-hot-toast';

const Learning: React.FC = () => {
  const [sections, setSections] = useState<LearningSection[]>([]);
  const [teams, setTeams] = useState<Team[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedTeam, setSelectedTeam] = useState<string>('');
  const [currentPage, setCurrentPage] = useState(1);
  
  // Modal states
  const [isSectionModalOpen, setIsSectionModalOpen] = useState(false);
  const [isVideoModalOpen, setIsVideoModalOpen] = useState(false);
  const [isContentModalOpen, setIsContentModalOpen] = useState(false);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [editingSection, setEditingSection] = useState<LearningSection | null>(null);
  const [selectedSection, setSelectedSection] = useState<LearningSection | null>(null);
  const [videoSectionId, setVideoSectionId] = useState<string | null>(null);
  const [contentSectionId, setContentSectionId] = useState<string | null>(null);
  
  // Delete states
  const [sectionToDelete, setSectionToDelete] = useState<LearningSection | null>(null);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const sectionsPerPage = 12;

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setIsLoading(true);
      const [sectionsData, teamsData] = await Promise.all([
        learningAPI.getAllSections(),
        teamsAPI.getAll(),
      ]);
      setSections(sectionsData);
      setTeams(teamsData);
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Failed to load learning sections');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateSection = async (sectionData: any) => {
    try {
      setIsSubmitting(true);
      const newSection = await learningAPI.createSection(sectionData);
      setSections([newSection, ...sections]);
      setIsSectionModalOpen(false);
      toast.success('Learning section created successfully');
    } catch (error) {
      console.error('Error creating section:', error);
      toast.error('Failed to create learning section');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUpdateSection = async (sectionData: any) => {
    if (!editingSection) return;
    
    try {
      setIsSubmitting(true);
      const updatedSection = await learningAPI.updateSection(editingSection.id, sectionData);
      setSections(sections.map(section => section.id === editingSection.id ? updatedSection : section));
      setIsSectionModalOpen(false);
      setEditingSection(null);
      toast.success('Learning section updated successfully');
    } catch (error) {
      console.error('Error updating section:', error);
      toast.error('Failed to update learning section');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteSection = (section: LearningSection) => {
    setSectionToDelete(section);
    setIsDeleteDialogOpen(true);
  };

  const confirmDeleteSection = async () => {
    if (!sectionToDelete) return;

    try {
      setIsSubmitting(true);
      await learningAPI.deleteSection(sectionToDelete.id);
      setSections(sections.filter(section => section.id !== sectionToDelete.id));
      setIsDeleteDialogOpen(false);
      setSectionToDelete(null);
      toast.success('Learning section deleted successfully');
    } catch (error) {
      console.error('Error deleting section:', error);
      toast.error('Failed to delete learning section');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleViewSection = (section: LearningSection) => {
    setSelectedSection(section);
    setIsDetailModalOpen(true);
  };

  const handleAddVideo = (sectionId: string) => {
    setVideoSectionId(sectionId);
    setIsVideoModalOpen(true);
  };

  const handleAddContent = (sectionId: string) => {
    setContentSectionId(sectionId);
    setIsContentModalOpen(true);
  };

  const handleContentSubmit = async (contentData: FormData) => {
    try {
      setIsSubmitting(true);
      await learningAPI.addContent(contentData);
      await loadData(); // Reload sections to show new content
      setIsContentModalOpen(false);
      setContentSectionId(null);
      toast.success('Learning content added successfully');
    } catch (error) {
      console.error('Error adding content:', error);
      toast.error('Failed to add learning content');
    } finally {
      setIsSubmitting(false);
    }
  };

  const openEditModal = (section: LearningSection) => {
    setEditingSection(section);
    setIsSectionModalOpen(true);
  };

  const closeModal = () => {
    setIsSectionModalOpen(false);
    setEditingSection(null);
  };

  // Filter sections
  const filteredSections = sections.filter(section => {
    const matchesSearch = section.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         section.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         section.team.name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesTeam = !selectedTeam || section.teamId === selectedTeam;
    return matchesSearch && matchesTeam;
  });

  // Pagination logic
  const totalPages = Math.ceil(filteredSections.length / sectionsPerPage);
  const startIndex = (currentPage - 1) * sectionsPerPage;
  const paginatedSections = filteredSections.slice(startIndex, startIndex + sectionsPerPage);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  if (isLoading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <LoadingSpinner size="lg" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <AcademicCapIcon className="h-8 w-8 text-primary-600" />
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Learning Management</h1>
              <p className="text-gray-600">Manage learning sections and video content for teams</p>
            </div>
          </div>
          <button
            onClick={() => setIsSectionModalOpen(true)}
            className="btn-primary flex items-center space-x-2"
          >
            <PlusIcon className="h-4 w-4" />
            <span>New Section</span>
          </button>
        </div>

        {/* Filters */}
        <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                <input
                  type="text"
                  placeholder="Search sections..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
            </div>
            <div className="sm:w-64">
              <select
                value={selectedTeam}
                onChange={(e) => setSelectedTeam(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
              >
                <option value="">All Teams</option>
                {teams.map((team) => (
                  <option key={team.id} value={team.id}>
                    {team.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <AcademicCapIcon className="h-8 w-8 text-blue-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">Total Sections</p>
                <p className="text-2xl font-semibold text-gray-900">{sections.length}</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <VideoCameraIcon className="h-8 w-8 text-green-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">Total Content</p>
                <p className="text-2xl font-semibold text-gray-900">
                  {sections.reduce((total, section) => total + (section._count.videos || 0) + (section._count.content || 0), 0)}
                </p>
              </div>
            </div>
          </div>
          
          <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <UserGroupIcon className="h-8 w-8 text-purple-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">Teams with Access</p>
                <p className="text-2xl font-semibold text-gray-900">
                  {new Set(sections.map(section => section.teamId)).size}
                </p>
              </div>
            </div>
          </div>
          
          <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <PlayIcon className="h-8 w-8 text-red-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">Avg Content/Section</p>
                <p className="text-2xl font-semibold text-gray-900">
                  {sections.length > 0 ? Math.round(sections.reduce((total, section) => total + (section._count.videos || 0) + (section._count.content || 0), 0) / sections.length) : 0}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Learning Sections Grid */}
        {filteredSections.length === 0 ? (
          <div className="text-center py-12">
            <AcademicCapIcon className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">No learning sections</h3>
            <p className="mt-1 text-sm text-gray-500">
              Get started by creating a new learning section.
            </p>
            <div className="mt-6">
              <button
                onClick={() => setIsSectionModalOpen(true)}
                className="btn-primary"
              >
                <PlusIcon className="h-4 w-4 mr-2" />
                New Section
              </button>
            </div>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {paginatedSections.map((section) => (
                <motion.div
                  key={section.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="bg-white rounded-lg shadow-sm border border-gray-200 hover:shadow-md transition-shadow"
                >
                  <div className="p-6">
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex-1">
                        <h3 className="text-lg font-semibold text-gray-900 mb-2">{section.name}</h3>
                        <div 
                          className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium mb-3"
                          style={{ 
                            backgroundColor: `${section.team.color}20`, 
                            color: section.team.color 
                          }}
                        >
                          <UserGroupIcon className="w-3 h-3 mr-1" />
                          {section.team.name}
                        </div>
                      </div>
                      <div className="flex space-x-1">
                        <button
                          onClick={() => handleViewSection(section)}
                          className="p-1 text-blue-600 hover:text-blue-800 transition-colors"
                          title="View Details"
                        >
                          <EyeIcon className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleAddVideo(section.id)}
                          className="p-1 text-green-600 hover:text-green-800 transition-colors"
                          title="Add Video"
                        >
                          <VideoCameraIcon className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleAddContent(section.id)}
                          className="p-1 text-purple-600 hover:text-purple-800 transition-colors"
                          title="Add Content (PDF, PPTX, Images)"
                        >
                          <PlusIcon className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => openEditModal(section)}
                          className="p-1 text-primary-600 hover:text-primary-800 transition-colors"
                          title="Edit Section"
                        >
                          <PencilIcon className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleDeleteSection(section)}
                          className="p-1 text-red-600 hover:text-red-800 transition-colors"
                          title="Delete Section"
                        >
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      </div>
                    </div>

                    {section.description && (
                      <p className="text-gray-600 text-sm mb-4 line-clamp-2">{section.description}</p>
                    )}
                    
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-500">Content</span>
                      <span className="font-medium text-gray-900 flex items-center">
                        <AcademicCapIcon className="w-4 h-4 mr-1" />
                        {(section._count.videos || 0) + (section._count.content || 0)}
                      </span>
                    </div>

                    <div className="flex items-center justify-between text-sm mt-2">
                      <span className="text-gray-500">Created</span>
                      <span className="font-medium text-gray-900">
                        {new Date(section.createdAt).toLocaleDateString()}
                      </span>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between">
                <div className="text-sm text-gray-700">
                  Showing {startIndex + 1} to {Math.min(startIndex + sectionsPerPage, filteredSections.length)} of {filteredSections.length} sections
                </div>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Previous
                  </button>
                  
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                    <button
                      key={page}
                      onClick={() => handlePageChange(page)}
                      className={`px-3 py-2 text-sm font-medium rounded-md ${
                        currentPage === page
                          ? 'text-primary-600 bg-primary-50 border border-primary-300'
                          : 'text-gray-500 bg-white border border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      {page}
                    </button>
                  ))}

                  <button
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </>
        )}

        {/* Learning Section Modal */}
        <LearningSectionModal
          isOpen={isSectionModalOpen}
          onClose={closeModal}
          onSubmit={editingSection ? handleUpdateSection : handleCreateSection}
          section={editingSection}
          teams={teams}
          isLoading={isSubmitting}
        />

        {/* Learning Video Modal */}
        <LearningVideoModal
          isOpen={isVideoModalOpen}
          onClose={() => {
            setIsVideoModalOpen(false);
            setVideoSectionId(null);
          }}
          onSubmit={async (videoData) => {
            try {
              setIsSubmitting(true);
              await learningAPI.addVideo({ ...videoData, sectionId: videoSectionId });
              await loadData(); // Reload to update video counts
              setIsVideoModalOpen(false);
              setVideoSectionId(null);
              toast.success('Video added successfully');
            } catch (error) {
              console.error('Error adding video:', error);
              toast.error('Failed to add video');
            } finally {
              setIsSubmitting(false);
            }
          }}
          sectionId={videoSectionId}
          isLoading={isSubmitting}
        />

        {/* Learning Content Modal */}
        <LearningContentModal
          isOpen={isContentModalOpen}
          onClose={() => {
            setIsContentModalOpen(false);
            setContentSectionId(null);
          }}
          onSubmit={handleContentSubmit}
          sectionId={contentSectionId || ''}
          isLoading={isSubmitting}
        />

        {/* Learning Section Detail Modal */}
        <LearningSectionDetailModal
          isOpen={isDetailModalOpen}
          onClose={() => {
            setIsDetailModalOpen(false);
            setSelectedSection(null);
          }}
          section={selectedSection}
          onSectionUpdate={(updatedSection: any) => {
            setSections(sections.map(section => section.id === updatedSection.id ? updatedSection : section));
          }}
          onDataChange={loadData}
        />

        {/* Delete Confirmation Dialog */}
        <ConfirmDialog
          isOpen={isDeleteDialogOpen}
          onClose={() => {
            setIsDeleteDialogOpen(false);
            setSectionToDelete(null);
          }}
          onConfirm={confirmDeleteSection}
          title="Delete Learning Section"
          message={`Are you sure you want to delete "${sectionToDelete?.name}"? This will also delete all videos in this section. This action cannot be undone.`}
          confirmText="Delete"
          cancelText="Cancel"
          type="danger"
          isLoading={isSubmitting}
        />
      </div>
    </Layout>
  );
};

export default Learning;
